import { Form, Input } from 'antd';
import { EOnboaringFormStep } from 'libs/constants/src/lib/onboarding';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormValidation } from '../../../hooks/useFormValidation';
import { MaxHeightWrapper, StepWrapper } from '../../../styles';
import { TranslatedTitle } from '../components';

import { CenterFormItem, CourseName, ErrorMessage, SubjectsWrapper } from './SubjectNotes.styles';

import { LoadingCircleSpinner } from '@/components/Loaders';
import { Box } from '@/components/Utils';
import { useOnboardingFormLogic, useRegionalization } from '@/hooks';
import { useMySubscriptions } from '@/store';

export const SubjectNotes = () => {
  const { t } = useTranslation('StudentOnboardingPageTranslations');
  const { planificableSubscriptions, isLoading } = useMySubscriptions();
  const { isButtonDisabled } = useFormValidation();
  const { currentStep } = useOnboardingFormLogic();
  const { uni: uniRegionConfig } = useRegionalization();
  const form = Form.useFormInstance();
  useEffect(() => {
    if (planificableSubscriptions?.length) {
      const existingSubjects = (form.getFieldValue(['userInfo', 'subjects']) || []) as Array<{
        course?: string;
        note?: number | string;
      }>;

      const noteByCourseId = new Map<string, number | string | undefined>();
      existingSubjects.forEach(subject => {
        if (subject?.course) {
          noteByCourseId.set(subject.course, subject.note);
        }
      });

      const normalizedSubjects = planificableSubscriptions.map(sub => ({
        course: sub.course._id,
        note: noteByCourseId.get(sub.course._id)
      }));

      form.setFieldsValue({
        userInfo: {
          subjects: normalizedSubjects
        }
      });
    }
  }, [planificableSubscriptions]);

  return (
    <StepWrapper>
      <TranslatedTitle i18nKey="SUBJECT_NOTES" />
      <MaxHeightWrapper>
        <SubjectsWrapper>
          {isLoading ? (
            <LoadingCircleSpinner size={24} />
          ) : (
            <>
              {planificableSubscriptions?.map((subscription, index) => (
                <Box
                  key={subscription._id}
                  justifyContent="space-between"
                  flexDirection="row"
                  gap={12}
                  alignItems="center">
                  <CourseName>
                    {subscription.course.emoji} {subscription.course.name}
                  </CourseName>
                  <CenterFormItem
                    name={['userInfo', 'subjects', index, 'note']}
                    rules={[
                      {
                        validator: async (_, value) => {
                          if (!value) return Promise.resolve();
                          const numValue = Number(value);
                          const { min, max } = uniRegionConfig.grade;
                          if (isNaN(numValue) || numValue < min || numValue > max) {
                            return Promise.reject('');
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}>
                    <Input
                      width="90px"
                      type="number"
                      min={uniRegionConfig.grade.min}
                      max={uniRegionConfig.grade.max}
                      placeholder={`${uniRegionConfig.grade.min}-${uniRegionConfig.grade.max}`}
                    />
                  </CenterFormItem>
                  <Form.Item name={['userInfo', 'subjects', index, 'course']} hidden>
                    <Input />
                  </Form.Item>
                </Box>
              ))}
            </>
          )}
        </SubjectsWrapper>
      </MaxHeightWrapper>

      {isButtonDisabled && currentStep === EOnboaringFormStep.SUBJECT_NOTES && (
        <ErrorMessage>
          {t('VALUE_BETWEEN_MIN_AND_MAX', {
            min: uniRegionConfig.grade.min,
            max: uniRegionConfig.grade.max
          })}
        </ErrorMessage>
      )}
    </StepWrapper>
  );
};
